/**
 * @file RbtVsBtVsBcbaVsBcaba.jsx
 * @description Standalone educational SEO page at /rbt-vs-bt-vs-bcba-vs-bcaba —
 *   the side-by-side comparison page (a real table: role, education, training or
 *   fieldwork, who supervises, typical first step; stacked cards on phones).
 *   Explains, at a general level, the difference between the RBT, BT, BCBA, and
 *   BCaBA titles used in ABA services: which are BACB credentials, which is just
 *   a common job title, and how supervision typically works between them. This is
 *   an educational overview, not legal or scope-of-practice advice — the page
 *   repeatedly directs readers to confirm current requirements with the BACB and
 *   applicable employers/regulators. Mirrors the structure of AboutAbaCases.jsx.
 * @module components/RbtVsBtVsBcbaVsBcaba
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  UsersIcon, ShieldIcon,
  AwardIcon, BriefcaseIcon, AlertTriangleIcon, ExternalLinkIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import HeroAlertsCta from "./HeroAlertsCta";
import GuideReviewStamp from "./GuideReviewStamp";
import { GuideYourPath } from "./GuideShared";
import { useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

export default function RbtVsBtVsBcbaVsBcaba({ onNavigate }) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  usePageSeo({
    title: "RBT vs BT vs BCBA vs BCaBA | ABA Roles Explained",
    description: "Understand common ABA provider roles, including RBTs, BTs, BCBAs, and BCaBAs, and how role, credential, supervision, and case fit affect ABA work.",
    canonical: "/rbt-vs-bt-vs-bcba-vs-bcaba",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-rbt-vs-bt-vs-bcba-vs-bcaba", [
    { name: "Home", url: "/" },
    { name: "RBT vs BT vs BCBA vs BCaBA", url: "/rbt-vs-bt-vs-bcba-vs-bcaba" },
  ]);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as AboutAbaCases / CityRolePage.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  const roleCards = [
    {
      Icon: UsersIcon,
      title: t('rbtVsBtVsBcbaVsBcaba.roleCardRbtTitle'),
      body: t('rbtVsBtVsBcbaVsBcaba.roleCardRbtBody'),
    },
    {
      Icon: BriefcaseIcon,
      title: t('rbtVsBtVsBcbaVsBcaba.roleCardBtTitle'),
      body: t('rbtVsBtVsBcbaVsBcaba.roleCardBtBody'),
    },
    {
      Icon: ShieldIcon,
      title: t('rbtVsBtVsBcbaVsBcaba.roleCardBcabaTitle'),
      body: t('rbtVsBtVsBcbaVsBcaba.roleCardBcabaBody'),
    },
    {
      Icon: AwardIcon,
      title: t('rbtVsBtVsBcbaVsBcaba.roleCardBcbaTitle'),
      body: t('rbtVsBtVsBcbaVsBcaba.roleCardBcbaBody'),
    },
  ];

  // Side-by-side table rows. Category-level facts only (no hours or fees);
  // the BACB pages stay the source of truth for exact requirements.
  const compareRows = ["Rbt", "Bt", "Bcaba", "Bcba"].map((k) => ({
    key: k,
    role: t(`rbtVsBtVsBcbaVsBcaba.tableRow${k}`),
    cells: [
      ["tableColEducation", t(`rbtVsBtVsBcbaVsBcaba.table${k}Education`)],
      ["tableColTraining", t(`rbtVsBtVsBcbaVsBcaba.table${k}Training`)],
      ["tableColSupervisor", t(`rbtVsBtVsBcbaVsBcaba.table${k}Supervisor`)],
      ["tableColFirstStep", t(`rbtVsBtVsBcbaVsBcaba.table${k}FirstStep`)],
    ],
  }));
  const thStyle = { textAlign: "left", fontSize: 12.5, fontWeight: 800, color: NAVY, letterSpacing: "0.02em", padding: "12px 14px", background: "#ede9fe", borderBottom: `1px solid ${BORDER}`, verticalAlign: "bottom" };
  const tdStyle = { fontSize: 14, color: TEXT_MID, lineHeight: 1.55, padding: "14px", borderBottom: `1px solid ${BORDER}`, verticalAlign: "top" };

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t('rbtVsBtVsBcbaVsBcaba.crumbHome'), onClick: () => onNavigate("home") },
            { label: t('rbtVsBtVsBcbaVsBcaba.crumbCurrent') },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t('rbtVsBtVsBcbaVsBcaba.heroBadgeLabel')}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>{t('rbtVsBtVsBcbaVsBcaba.heroBadgeDomain')}</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t('rbtVsBtVsBcbaVsBcaba.heroTitle')}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t('rbtVsBtVsBcbaVsBcaba.heroSubtitle')}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 640, margin: "0 0 24px" }}>
            {t('rbtVsBtVsBcbaVsBcaba.heroBody')}
          </p>
          <HeroAlertsCta label={t("guideShared.ctaAlerts")} pagePath="/rbt-vs-bt-vs-bcba-vs-bcaba/" onNavigate={onNavigate} isMobile={isMobile} />
        </div>
      </header>

      {/* ── Side-by-side table ────────────────────────────────── */}
      <section data-testid="compare-section" style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 12px", letterSpacing: "-0.02em" }}>
            {t('rbtVsBtVsBcbaVsBcaba.tableTitle')}
          </h2>
          <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.7, marginBottom: 20 }}>
            {t('rbtVsBtVsBcbaVsBcaba.tableIntro')}
          </p>
          {isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {compareRows.map((r) => (
                <div key={r.key} data-testid="compare-card" style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "16px 18px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                  <div style={{ fontSize: 17, fontWeight: 900, color: NAVY, marginBottom: 8 }}>{r.role}</div>
                  {r.cells.map(([colKey, val]) => (
                    <div key={colKey} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO, letterSpacing: "0.03em", textTransform: "uppercase" }}>{t(`rbtVsBtVsBcbaVsBcaba.${colKey}`)}</div>
                      <div style={{ fontSize: 14, color: TEXT_MID, lineHeight: 1.55 }}>{val}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ overflowX: "auto", border: `1px solid ${BORDER}`, borderRadius: 14 }}>
              <table data-testid="compare-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th scope="col" style={thStyle}>{t('rbtVsBtVsBcbaVsBcaba.tableColRole')}</th>
                    {compareRows[0].cells.map(([colKey]) => (
                      <th key={colKey} scope="col" style={thStyle}>{t(`rbtVsBtVsBcbaVsBcaba.${colKey}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map((r) => (
                    <tr key={r.key}>
                      <th scope="row" style={{ ...tdStyle, textAlign: "left", fontSize: 15, fontWeight: 900, color: NAVY }}>{r.role}</th>
                      {r.cells.map(([colKey, val]) => (
                        <td key={colKey} style={tdStyle}>{val}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* ── 1. The four terms, explained ──────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('rbtVsBtVsBcbaVsBcaba.section1Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 22 }}>
            {t('rbtVsBtVsBcbaVsBcaba.section1Intro')}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16 }}>
            {roleCards.map((c) => (
              <div key={c.title} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={19} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 5 }}>{c.title}</div>
                  <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{c.body}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 14, color: TEXT_MID, lineHeight: 1.7, marginTop: 18 }}>
            {t('rbtVsBtVsBcbaVsBcaba.section1LinksIntro')}{" "}
            <SeoLink to="how-to-become-an-rbt" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtVsBtVsBcbaVsBcaba.linkHowToBecomeRbt')}</SeoLink>,{" "}
            <SeoLink to="how-to-become-a-bcba" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtVsBtVsBcbaVsBcaba.linkHowToBecomeBcba')}</SeoLink>, {t('rbtVsBtVsBcbaVsBcaba.section1LinksOr')}{" "}
            <SeoLink to="how-to-become-a-bcaba" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtVsBtVsBcbaVsBcaba.linkHowToBecomeBcaba')}</SeoLink>. {t('rbtVsBtVsBcbaVsBcaba.section1LinksBridge')}{" "}
            <SeoLink to="aba-roles-guide" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtVsBtVsBcbaVsBcaba.linkAbaRolesGuide')}</SeoLink>.
          </p>
        </div>
      </section>

      {/* ── 2. "BT" is usually a job title, not a credential ──── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('rbtVsBtVsBcbaVsBcaba.section2Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('rbtVsBtVsBcbaVsBcaba.section2Body1Part1')} <strong>{t('rbtVsBtVsBcbaVsBcaba.section2Body1Strong1')}</strong> {t('rbtVsBtVsBcbaVsBcaba.section2Body1Part2')} <strong>{t('rbtVsBtVsBcbaVsBcaba.section2Body1Strong2')}</strong> {t('rbtVsBtVsBcbaVsBcaba.section2Body1Part3')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('rbtVsBtVsBcbaVsBcaba.section2Body2')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('rbtVsBtVsBcbaVsBcaba.section2Body3')}
          </p>
        </div>
      </section>

      {/* ── 3. How supervision typically works ────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('rbtVsBtVsBcbaVsBcaba.section3Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('rbtVsBtVsBcbaVsBcaba.section3Body1')}
          </p>
        </div>
      </section>

      {/* ── 4. Disclaimer ──────────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: isMobile ? "20px" : "24px 26px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <AlertTriangleIcon size={19} color="#b45309" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: NAVY, marginBottom: 8 }}>{t('rbtVsBtVsBcbaVsBcaba.disclaimerTitle')}</div>
              <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, margin: "0 0 10px" }}>
                {t('rbtVsBtVsBcbaVsBcaba.disclaimerBodyPart1')}{" "}
                <a href="https://www.bacb.com/" target="_blank" rel="noopener noreferrer" style={{ color: INDIGO, fontWeight: 700 }}>
                  {t('rbtVsBtVsBcbaVsBcaba.disclaimerBacbLinkText')} <ExternalLinkIcon size={12} color={INDIGO} style={{ display: "inline", verticalAlign: "middle" }} />
                </a>{" "}
                {t('rbtVsBtVsBcbaVsBcaba.disclaimerBodyPart2')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. How ABAcases helps ────────────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('rbtVsBtVsBcbaVsBcaba.section5Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('rbtVsBtVsBcbaVsBcaba.section5Body1Part1')}{" "}
            <SeoLink to="aba-case-finder" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtVsBtVsBcbaVsBcaba.linkAbaCaseFinder')}</SeoLink>{" "}
            {t('rbtVsBtVsBcbaVsBcaba.section5Body1Part2')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('rbtVsBtVsBcbaVsBcaba.section5Body2Part1')}{" "}
            <SeoLink to="post-aba-cases" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtVsBtVsBcbaVsBcaba.linkPostOpenCases')}</SeoLink>{" "}
            {t('rbtVsBtVsBcbaVsBcaba.section5Body2Part2')}{" "}
            <SeoLink to="aba-provider-waitlist" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtVsBtVsBcbaVsBcaba.linkJoinAlerts')}</SeoLink>{" "}
            {t('rbtVsBtVsBcbaVsBcaba.section5Body2Part3')}{" "}
            <SeoLink to="about-aba-cases" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtVsBtVsBcbaVsBcaba.linkAboutAbaCases')}</SeoLink>.
          </p>
        </div>
      </section>

      <GuideYourPath current="rbt-vs-bt-vs-bcba-vs-bcaba" onNavigate={onNavigate} isMobile={isMobile} />

      <GuideReviewStamp slug="rbt-vs-bt-vs-bcba-vs-bcaba" />
      <PublicFooter />
    </div>
  );
}
